// =============================================================================
// modules/checkout-preselect.js — S8 checkout (user decision 2026-09-11).
//
// The booking wizard ends with `…/checkout/?payment_method={gateway id}`
// (blocks/treatments-booking/view.js#payAndConfirm). This module honours that
// query param by checking the matching gateway radio on the checkout page.
//
// WHAT ACTUALLY RENDERS AT /checkout/ (inspected, not assumed):
//   · the child theme has NO `templates/page-checkout.html`, so the FSE
//     hierarchy gives page 7 (slug `checkout`) the PARENT's
//     `ai-zippy/templates/page-checkout.html`, whose body is
//     `<!-- wp:shortcode -->[ai_zippy_checkout]<!-- /wp:shortcode -->`;
//   · `CheckoutShortcode::render()` (ai-zippy/inc/Checkout/CheckoutShortcode.php
//     :58-77) prints `<div id="ai-zippy-checkout">` because
//     `CheckoutSettings::isReact()` is true — the option
//     `ai_zippy_checkout_template` is unset and its DEFAULT is `'react'`
//     (ai-zippy/inc/Checkout/CheckoutSettings.php:16-41). Verified on this site
//     with `wp option get` (unset) + `isReact(): true`;
//   · so /checkout/ is the parent's REACT app, not the Woo checkout block and
//     not the classic shortcode. Its radios are
//     `label.zk__radio-option > input.zk__radio-input[name=payment]` and carry
//     **no value/id attribute** (checkout/components/PaymentMethods.jsx:38-48),
//     so the gateway id is only recoverable from the label text via the
//     localized `window.aiZippyCheckout.paymentGateways` array
//     (ai-zippy/inc/Checkout/CheckoutAssets.php:319-325);
//   · `CheckoutSection.jsx:72-74` mounts children only while the section is
//     `editing`, and `CheckoutApp.jsx:68` defaults to `"cod"` and reads no URL
//     param — so the payment radios do not exist until the guest finishes
//     Contact + Billing. This module therefore observes the DOM until they do.
//
// A `input[type=radio][value="{id}"]` fast path runs first so the same snippet
// also works if the checkout is ever switched to the Woo block or the classic
// shortcode. Setting `.checked` alone would be ignored by React (state drives
// it), so a real `.click()` is dispatched — that is what fires React's onChange
// → `setPaymentMethod(id)` → `payment_method` in the order payload
// (checkout/api.js:112).
//
// Defensive no-op when the param is absent, when no checkout container exists,
// when no gateway matches, or once the click has been dispatched (so it never
// fights a guest who changes the method by hand afterwards).
// Vanilla JS, no jQuery.
// =============================================================================

/** Milliseconds to keep watching for the payment radios to mount. */
var WATCH_MS = 120000;

function readWanted() {
	try {
		return (new URLSearchParams(window.location.search).get("payment_method") || "").trim();
	} catch (e) {
		return "";
	}
}

function checkoutRoot() {
	return (
		document.getElementById("ai-zippy-checkout") ||
		document.querySelector(".wc-block-checkout") ||
		document.querySelector("form.checkout") ||
		null
	);
}

/** Gateway ids are `sanitize_key()` output, but escape defensively anyway. */
function escapeId(id) {
	var css = window.CSS;
	if (css && typeof css.escape === "function") {
		return css.escape(id);
	}
	return String(id).replace(/["\\]/g, "\\$&");
}

/** id → title, from the array the parent localizes for its React checkout. */
function gatewayTitle(id) {
	var list = (window.aiZippyCheckout && window.aiZippyCheckout.paymentGateways) || [];
	for (var i = 0; i < list.length; i++) {
		if (list[i] && list[i].id === id) {
			return String(list[i].title || "").trim();
		}
	}
	return "";
}

/**
 * Check the radio for `id` inside `scope`.
 * Returns true when the selection has been made (or dispatched), false when
 * nothing matching is on screen yet.
 */
function apply(id, scope) {
	// 1. Woo checkout block / classic shortcode: radios carry the gateway id.
	var byValue = null;
	try {
		byValue = scope.querySelector('input[type="radio"][value="' + escapeId(id) + '"]');
	} catch (e) {
		byValue = null; // a malformed id must never break the checkout
	}
	if (byValue) {
		if (!byValue.checked) {
			byValue.click();
		}
		return true;
	}

	// 2. Parent React checkout: match on the gateway title (the input has no id).
	var title = gatewayTitle(id);
	if (!title) {
		return false;
	}
	var labels = scope.querySelectorAll(".zk__radio-label");
	for (var i = 0; i < labels.length; i++) {
		if ((labels[i].textContent || "").trim() !== title) {
			continue;
		}
		var option = labels[i].closest(".zk__radio-option");
		var input = option && option.querySelector('input[type="radio"]');
		if (!input) {
			return false;
		}
		if (!input.checked) {
			// A real click, not `.checked = true`: React owns the value and only
			// onChange → setPaymentMethod(id) changes what the order is placed with.
			input.click();
		}
		return true;
	}

	return false;
}

export function initCheckoutPreselect() {
	var wanted = readWanted();
	if (!wanted) {
		return; // no param → nothing to do
	}

	var initialRoot = checkoutRoot();
	if (!initialRoot) {
		return; // not a checkout DOM → nothing to do
	}

	if (apply(wanted, initialRoot)) {
		return; // already on screen (e.g. the Woo block renders it immediately)
	}

	// The React radios mount only when the payment section becomes active.
	var finished = false;
	var observer = null;
	var timer = window.setTimeout(finish, WATCH_MS);

	function finish() {
		if (finished) {
			return;
		}
		finished = true;
		window.clearTimeout(timer);
		if (observer) {
			observer.disconnect();
			observer = null;
		}
	}

	function attempt() {
		if (finished) {
			return;
		}
		if (apply(wanted, checkoutRoot() || initialRoot)) {
			finish();
		}
	}

	if (typeof MutationObserver !== "function") {
		// No observer: a couple of delayed retries cover React's mount.
		window.setTimeout(attempt, 400);
		window.setTimeout(attempt, 1500);
		return;
	}

	observer = new MutationObserver(attempt);
	observer.observe(document.body, { childList: true, subtree: true });
}
